import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { ComicGridCard } from "@/components/base/ComicCard";
import StatusBadge from "@/components/base/StatusBadge";
import ChapterList from "./components/ChapterList";
import SharePanel from "./components/SharePanel";
import useFavorites from "@/hooks/useFavorites";
import {
  categorySlugMap,
  formatViews,
  getChapters,
  getComicById,
  relatedComics,
} from "@/utils/comics";

export default function Detail() {
  const { comicId } = useParams();
  const navigate = useNavigate();
  const comic = getComicById(comicId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");
  const [shareOpen, setShareOpen] = useState(false);

  const chapters = useMemo(() => (comic ? getChapters(comic) : []), [comic]);
  const related = useMemo(() => (comic ? relatedComics(comic) : []), [comic]);

  if (!comic) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-20 md:px-6">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="没有找到这部韩漫"
          desc="该作品可能已下架或链接有误，返回首页看看其他精彩韩漫吧。"
          actionLabel="返回首页"
          onAction={() => navigate("/")}
        />
      </div>
    );
  }

  const fav = isFavorite(comic.id);

  const handleFavorite = () => {
    const added = toggleFavorite(comic.id);
    setToast(added ? "已加入收藏（本地演示）" : "已取消收藏");
    setTimeout(() => setToast(""), 2200);
  };

  return (
    <>
      <Seo
        title={`${comic.title} - 免费韩漫在线阅读`}
        description={`${comic.summary.slice(0, 80)}…《${comic.title}》由 ${comic.author} 创作，${comic.status}，共 ${comic.chapterCount} 话，支持免费在线阅读。`}
        keywords={`${comic.title},${comic.author},${comic.categories.join(",")},韩漫免费观看,在线阅读`}
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb
          items={[
            {
              label: `${comic.categories[0]}分类`,
              to: `/category/${categorySlugMap[comic.categories[0]] ?? "romance"}`,
            },
            { label: comic.title },
          ]}
        />

        <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <div className="mx-auto w-full max-w-[260px] overflow-hidden rounded-lg bg-background-200 lg:mx-0">
              <div className="aspect-[3/4] w-full">
                <img
                  src={comic.cover}
                  alt={`${comic.title} 韩漫封面`}
                  title={`${comic.title} 免费韩漫在线阅读`}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge comic={comic} />
              {comic.categories.map((cat) => (
                <span
                  key={cat}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
                >
                  {cat}
                </span>
              ))}
              {comic.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="mt-3 font-display text-2xl font-extrabold text-foreground-950 md:text-4xl">
              {comic.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line text-primary-600" />
                作者：{comic.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-book-marked-line text-primary-600" />
                共 {comic.chapterCount} 话
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line text-primary-600" />
                {formatViews(comic.views)} 次阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-star-fill text-primary-500" />
                {comic.rating} 分
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-primary-600" />
                更新于 {comic.updatedAt}
              </span>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to={`/read/${comic.id}/1`}
                className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                从第一话开始
              </Link>
              <Link
                to={`/read/${comic.id}/${comic.chapterCount}`}
                className="whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                阅读最新话
              </Link>
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-2.5 text-sm font-medium transition-colors ${
                  fav
                    ? "border-primary-300 bg-primary-50 text-primary-700"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                <i className={fav ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                {fav ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="text-sm font-semibold text-foreground-950">漫画简介</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                {comic.summary}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 space-y-8">
          <ChapterList comicId={comic.id} chapters={chapters} />

          <section>
            <h2 className="mb-4 text-lg font-bold text-foreground-950">相关推荐</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {related.map((item) => (
                <ComicGridCard key={item.id} comic={item} />
              ))}
            </div>
          </section>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[70] -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}

      <SharePanel
        open={shareOpen}
        url={`${window.location.origin}/detail/${comic.id}`}
        title={comic.title}
        onClose={() => setShareOpen(false)}
      />
    </>
  );
}